import { Routes, Route, Outlet } from "react-router-dom";
import { ApiError } from "@touchscreen/api-client";
import { useTouchscreenContext, useBrand } from "@touchscreen/hooks";
import { useBrandModule } from "@touchscreen/brand-kit";
import { BottomNav } from "./components/BottomNav.js";
import { NowPlayingBar } from "./components/NowPlayingBar.js";
import { BuildBadge } from "./components/BuildBadge.js";
import { HoldingScreen } from "./components/HoldingScreen.js";
import { BrowseScreen } from "./screens/BrowseScreen.js";
import { GroupScreen } from "./screens/GroupScreen.js";
import { SearchScreen } from "./screens/SearchScreen.js";
import { MyMusicScreen } from "./screens/MyMusicScreen.js";
import { clearLastVenueRef } from "./lib/lastVenue.js";

function Layout(): React.JSX.Element {
  return (
    <>
      <NowPlayingBar />
      <Outlet />
      <BottomNav />
      <BuildBadge />
    </>
  );
}

export interface PhoneShellProps {
  /** A resumed or mistyped venue ref that turns out not to exist — go back to Find Me, not an infinite retry loop. */
  onVenueInvalid: () => void;
  /** The legacy `/JUKE1234` entry URL is also the initial Browse route. */
  legacyVenuePath: boolean;
}

export function PhoneShell(props: PhoneShellProps): React.JSX.Element {
  const { boot } = useTouchscreenContext();
  const { brandKey } = useBrand();
  useBrandModule(brandKey);

  if (boot.status === "booting") {
    return <HoldingScreen message="Loading" />;
  }

  if (boot.status === "error") {
    const isVenueNotFound = boot.error instanceof ApiError && boot.error.code === "venue_not_found";
    if (isVenueNotFound) {
      return (
        <HoldingScreen message="We couldn't find that venue">
          <button
            className="holding-screen__retry"
            onClick={() => {
              clearLastVenueRef();
              props.onVenueInvalid();
            }}
          >
            Start over
          </button>
        </HoldingScreen>
      );
    }
    return <HoldingScreen message="One moment" detail="Reconnecting…" />;
  }

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<BrowseScreen />} />
        <Route path="/groups/:groupId" element={<GroupScreen />} />
        <Route path="/search" element={<SearchScreen />} />
        <Route path="/my-music" element={<MyMusicScreen />} />
        {props.legacyVenuePath ? <Route path="/:venueRef" element={<BrowseScreen />} /> : null}
      </Route>
    </Routes>
  );
}
